import { Box, Typography } from '@mui/material'
import { ID, Port, PortFlag } from '../../types/models'
import { EnergyStorageConfig, EnergyStorageDriver, EnergyStoragePort, PortConfigEnergyStorage, PortConfigEnergyStorageHuaweiHybridParams, PortConfigEnergyStorageParams, PortConfigEnergyStoragePlenticoreParams, PortConfigEnergyStorageSunspecParams } from '../../types/ports'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { Trans } from 'react-i18next'
import { ListSelector } from '../../components/atoms/ListSelector'
import { BatteryCharging90, Cable } from '@mui/icons-material'
import { BatteryOptimizerPortControl } from './BatteryOptimizerPortControl'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'
import { Text } from '../../components/atoms/Text'
import { HelpTitle } from '../../components/atoms/HelpTitle'

type EnergyStorageConnType =
  | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0'
  | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_1'
  | 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP'

/**
 * Default connection params for a given connection type.
 *
 * These drivers only carry conn_params over Modbus TCP (tcp_inst/keep_socket).
 * Their config struct has no baudrate/parity fields at all — the baud rate is
 * fixed per driver — so over RS485 the key is dropped entirely. Sending one
 * anyway makes the device read-back never equal the pending config, producing
 * endless confmit retries and a spurious config revert.
 */
export function connParamsForConnType(
  connType: EnergyStorageConnType,
): { tcp_inst: number; keep_socket: number } | undefined {
  return connType === 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP'
    ? { tcp_inst: 0, keep_socket: 1 }
    : undefined
}

export function driverDefaults(driver: EnergyStorageDriver): PortConfigEnergyStorageParams {
  switch (driver) {
    case 'SPP_ENERGYSTORAGE_DRIVER_SUNSPEC':
      return {
        conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
        conn_params: {
          baudrate: 'AUTO',
          parity: 'ODD',
        },
        em_slave_id: 0,
        slave_id: 1,
        extras: [
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
        ]
      } as PortConfigEnergyStorageSunspecParams

    case 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_HYBRID':
    case 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_ALT':
    case 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_EMMA':
    case 'SPP_ENERGYSTORAGE_DRIVER_FRONIUS_GEN24':
      return {
        conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP',
        conn_params: {
          tcp_inst: 0,
          keep_socket: 1
        },
        slave_id: 0,
        em_slave_id: driver === 'SPP_ENERGYSTORAGE_DRIVER_FRONIUS_GEN24' ? 200 : 0,
        extras: [
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
        ]
      }
    case 'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE':
      return {
        conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP',
        conn_params: {
          tcp_inst: 0,
          keep_socket: 0
        },
        slave_id: driver === 'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE' ? 71 : 1,
        em_slave_id: 0,
        extras: [
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
        ]
      }

    case 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_HYBRID':
    case 'SPP_ENERGYSTORAGE_DRIVER_GROWATT_HYBRID':
    case 'SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_HYBRID':
    case 'SPP_ENERGYSTORAGE_SUNGROW_HYBRID':
    case 'SPP_ENERGYSTORAGE_DRIVER_SOFAR_HYBRID':
    case 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_S6':
    case 'SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_F2':
    case 'SPP_ENERGYSTORAGE_DRIVER_1KOMMA5':
    case 'SPP_ENERGYSTORAGE_DRIVER_DYNESS_DH200':

      return {
        conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
        slave_id: driver === 'SPP_ENERGYSTORAGE_DRIVER_1KOMMA5' ? 247 : 1,
        em_slave_id: 0,
        extras: [
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
        ]
      }

    case 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_IES':
    case 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_G4':
      // Modbus TCP, RS485 port 0 and RS485 port 1 are all selectable.
      // em_slave_id is unused (kept 0 and hidden in the UI).
      // Default to RS485 port 0, which carries no conn_params at all.
      return {
        conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0',
        slave_id: 1,
        em_slave_id: 0,
        extras: [
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
        ]
      } as PortConfigEnergyStorageHuaweiHybridParams

    case 'SPP_ENERGYSTORAGE_DRIVER_SIGENERGY':
      // Modbus TCP (port 502) or RS485 9600 8N1. em_slave_id is the plant address
      // (247) used for the plant-level meter reads and control writes; the
      // individual inverter answers on slave_id (1-246).
      return {
        conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP',
        conn_params: connParamsForConnType('ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP'),
        slave_id: 1,
        em_slave_id: 247,
        extras: [
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
        ]
      } as PortConfigEnergyStorageHuaweiHybridParams

    case 'SPP_ENERGYSTORAGE_DRIVER_SUNGROW_POWERSTACK':
      // Modbus TCP only, straight to the LC300 local controller (slave_id 1).
      // em_slave_id is unused (kept 0 and hidden in the UI).
      return {
        conn_type: 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP',
        conn_params: connParamsForConnType('ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP'),
        slave_id: 1,
        em_slave_id: 0,
        extras: [
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
          {
            func_code: 0,
            reg_addr: 0,
          },
        ]
      } as PortConfigEnergyStorageHuaweiHybridParams
  }

  throw new Error(`Energy store driver defauts for '${driver}' not defined.`)
}

/**
 * Stored params completed with the driver defaults for any missing field.
 *
 * conn_params is not a plain default: it has to follow the stored conn_type.
 * Apart from the generic SunSpec driver, conn_params exists only over Modbus TCP
 * and the device echoes none over RS485. The server stores the same shape
 * (EnergyStorage.normalize/1), so a TCP default merged onto an RS485 port, or a
 * baudrate/parity pair left over from an old save, shows up as a false
 * "params.conn_params" difference and is replayed by the next save.
 */
export function paramsWithDriverDefaults(
  driver: EnergyStorageDriver,
  params: Partial<PortConfigEnergyStorageParams>,
): PortConfigEnergyStorageParams {
  const merged = { ...driverDefaults(driver), ...params } as Record<string, unknown>

  if (driver !== 'SPP_ENERGYSTORAGE_DRIVER_SUNSPEC') {
    const connParams = connParamsForConnType(merged.conn_type as EnergyStorageConnType)
    if (!connParams) {
      delete merged.conn_params
    } else if (!merged.conn_params) {
      merged.conn_params = connParams
    }
  }

  return merged as unknown as PortConfigEnergyStorageParams
}

/**
 * Controller for battery.
 */
export class EnergyStoragePortControl extends PortControl<EnergyStoragePort, EnergyStorageConfig> {

  port: EnergyStoragePort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as EnergyStoragePort

    // Ensure stored params include all driver-default fields (e.g. em_slave_id)
    // so they are never missing when building flash payloads.
    const config = this.port.config as unknown as Record<string, unknown>
    if (config.driver && config.params && typeof config.params === 'object') {
      config.params = paramsWithDriverDefaults(config.driver as EnergyStorageDriver, config.params as object)
    }
  }

  errorFlags(): PortFlag[] {
    return this.port.flags.concat(this.statusFlags())
  }

  /**
   * Error flags derived from the inverter communication status reported by the firmware.
   */
  statusFlags(): PortFlag[] {
    switch (this.getValue('status')) {
      case 'SPP_ENERGYSTORAGE_INFO_STATUS_COMM_FAILURE':
        return ['COMMUNICATIONS']
      case 'SPP_ENERGYSTORAGE_INFO_STATUS_FAIL_PUBLIC_INTERNET':
        return ['COMMUNICATIONS_PUBLIC_INTERNET']
      default:
        return []
    }
  }

  /**
   * On Modbus TCP a plain "no connection" tells the installer nothing; the
   * address the firmware is currently scanning tells them whether Enion and
   * the inverter are even on the same subnet.
   */
  errorMessages(): string[] {
    const ip = this.tcpScanAddress()
    return this.errorFlags().map(flag =>
      flag === 'COMMUNICATIONS' && ip
        ? this.app.t('Error-COMMUNICATIONS_TCP', { ip })
        : this.app.t(`Error-${flag}`)
    )
  }

  /**
   * The inverter address the firmware is currently trying over Modbus TCP, as
   * dotted quad, or undefined when it is not known.
   *
   * The firmware (modbus_start_connect_instance) copies the address of each
   * connection attempt into the first four bytes of the info frame's extra[],
   * but only on the TCP path; over RS485 the same bytes carry driver-specific
   * diagnostics, so the committed (flashed) connection type gates the read.
   * All-zero means no attempt has been made yet (e.g. no own IP from DHCP).
   */
  tcpScanAddress(): string | undefined {
    const params = this.port.config?.params as Partial<PortConfigEnergyStorageParams> | undefined
    if (params?.conn_type !== 'ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP') {
      return undefined
    }
    const extra = this.getValue<unknown>('extra')
    if (!Array.isArray(extra) || extra.length < 4) {
      return undefined
    }
    const octets = extra.slice(0, 4)
    if (!octets.every(o => Number.isInteger(o) && (o as number) >= 0 && (o as number) <= 255)) {
      return undefined
    }
    if (octets.every(o => o === 0)) {
      return undefined
    }
    return octets.join('.')
  }

  getDescription(): string { return this.app.t('Energy Storage') }

  getTypeName(): string { return this.app.t('PortType-EnergyStorage') }

  get group(): string { return this.app.t('PortGroup-Other') }

  getName(): string {
    return this.app.t('PortName-EnergyStorage {number}').replace('{number}', `${this.number}`)
  }

  getConfigDefaults(): PortConfigEnergyStorage {
    return {
      enabled: false,
      to_summer: false,
      driver: 'SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE',
      params: driverDefaults('SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE')
    }
  }

  optimizerControl(): BatteryOptimizerPortControl {
    const ctrl = this.deviceControl.findPortBy(portControl => portControl.port.type === 'BATTERY_OPTIMIZER')
    if (!ctrl) {
      throw new Error(`Cannot find battery optimizer control for port #${this.port.id}.`)
    }
    return ctrl as BatteryOptimizerPortControl
  }

  getAssociatedPortIDs(): ID[] {
    const opt = this.optimizerControl()
    // Only the legacy ES + optimizer (0x220) ports. The batopt2 (0x222) /
    // soc_plan (0x223) ports are deliberately NOT associated here: the config
    // page enables and flashes every associated port, but batopt2 conf is
    // firmware-owned (the server must not write it), and including them broke
    // the "Battery optimizing" appliance config page. Associating them to
    // battery appliances for the BatteryStateWidget is handled server-side by
    // Appliance.attach_batopt2_ports/0.
    return [this.port.id as ID, opt.port.id as ID]
  }

  configTopLeft() : React.ReactNode | undefined {

    const driver = this.getConfig('driver') as EnergyStorageDriver

    const onChangeDriver = (driver: string) => {

      this.setConfig('enabled', true)
      this.setConfig('driver', driver)

      const newParams = driverDefaults(driver as EnergyStorageDriver)

      this.setConfig('params', newParams)
    }

    return (
      <Box>
        <Trans>Inverter Model</Trans>

        <ListSelector
          items={{
            // SPP_ENERGYSTORAGE_DRIVER_SUNSPEC: '',
            // SPP_ENERGYSTORAGE_DRIVER_GENERIC_MODBUS: '',
            SPP_ENERGYSTORAGE_DRIVER_KOSTAL_PLENTICORE: 'Kostal Plenticore',
            SPP_ENERGYSTORAGE_DRIVER_SOLIS_HYBRID: 'Solis S5',
            SPP_ENERGYSTORAGE_DRIVER_GROWATT_HYBRID: 'Growatt',
            SPP_ENERGYSTORAGE_SUNGROW_HYBRID: 'Sungrow',
            SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_HYBRID: 'Ledvance F1',
            SPP_ENERGYSTORAGE_DRIVER_HUAWEI_HYBRID: 'Huawei',
            SPP_ENERGYSTORAGE_DRIVER_HUAWEI_ALT: 'Huawei alternative',
            SPP_ENERGYSTORAGE_DRIVER_HUAWEI_EMMA: 'Huawei MAP0+EMMA',
            SPP_ENERGYSTORAGE_DRIVER_SOFAR_HYBRID: 'Sofar',
            SPP_ENERGYSTORAGE_DRIVER_SOLIS_S6: 'Solis S6',
            SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_F2: 'Ledvance F2',
            SPP_ENERGYSTORAGE_DRIVER_FRONIUS_GEN24: 'Fronius Symo/Primo GEN24',
            SPP_ENERGYSTORAGE_DRIVER_1KOMMA5: '1komma5 Inverter',
            SPP_ENERGYSTORAGE_DRIVER_DYNESS_DH200: 'Dyness DH200',
            SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_IES: 'Solax X1-IES / X3-IES',
            SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_G4: 'Solax X1-G4 / X3-G4',
            SPP_ENERGYSTORAGE_DRIVER_SIGENERGY: 'Sigenergy',
            SPP_ENERGYSTORAGE_DRIVER_SUNGROW_POWERSTACK: 'Sungrow PowerStack',
          }}
          icons={[BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90, BatteryCharging90]}
          selected={driver || null}
          onSelect={onChangeDriver}
        />
      </Box>
    )
  }

  configTopRight() : React.ReactNode | undefined {
    const driver = this.getConfig('driver') as EnergyStorageDriver
    const params = (this.getConfig('params') || {}) as PortConfigEnergyStoragePlenticoreParams
    const toSummer = this.getConfig('to_summer')

    const onChangeParam = (param: string, value: any) => {
      // Read params fresh to avoid stale closure from concurrent re-renders.
      const currentParams = (this.getConfig('params') || {}) as PortConfigEnergyStorageParams
      this.setConfig('params', { ...currentParams, [param]: value })
    }

    // Changing the connection type must also fix up conn_params: Modbus TCP
    // carries tcp_inst/keep_socket, RS485 carries nothing. Otherwise the device
    // read-back never equals the pending config.
    const onChangeConnType = (connType: string) => {
      const currentParams = (this.getConfig('params') || {}) as PortConfigEnergyStorageParams
      const next: Record<string, unknown> = { ...currentParams, conn_type: connType }
      const connParams = connParamsForConnType(connType as EnergyStorageConnType)
      if (connParams) {
        next.conn_params = connParams
      } else {
        delete next.conn_params
      }
      this.setConfig('params', next)
    }

    // Commonly shared net measuring control.
    const Measuring = <Box sx={{ mb: 2 }}>
      <HelpTitle title={this.app.t('Net Metering')} code="ENERGY_STORAGE"/>

      <Box sx={{ display: 'flex' }}>
        <CustomSwitch data-cy="switch-To Summer" onClick={() => this.setConfig('to_summer', !toSummer)} checked={!!toSummer}/>
          <Typography component="span" sx={{ ml: 2 }}>
            <Trans>Part of building net metering?</Trans>
          </Typography>
      </Box>

      {!!toSummer && <Box sx={{ mt: 1, color: 'error.main' }}>
          <Trans>Are you sure? We recommend metering the whole supply if possible. Use this choice only if the energy meter does not measure the whole supply but loads only for example.</Trans>
        </Box>
      }

    </Box>

    // TODO: This is messy. Configure connectivity in clear manner somewhere else.
    if (driver === 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_HYBRID' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_GROWATT_HYBRID' ||
      driver === 'SPP_ENERGYSTORAGE_SUNGROW_HYBRID' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_HYBRID' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_HYBRID' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_ALT' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_EMMA' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_SOFAR_HYBRID' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_SOLIS_S6' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_LEDVANCE_F2' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_1KOMMA5' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_FRONIUS_GEN24' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_DYNESS_DH200' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_IES' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_G4' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_SIGENERGY' ||
      driver === 'SPP_ENERGYSTORAGE_DRIVER_SUNGROW_POWERSTACK') {

      let connectionItems: Record<string, string> = {
        ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_0:
          this.app.t('ConnType-RS485') +
          ' ' + this.app.t('Port {number}').replace('{number}', '0'),
        ENERGYSTORAGE_CONN_TYPE_MODBUS_RTU_RS485_1:
          this.app.t('ConnType-RS485') +
          ' ' + this.app.t('Port {number}').replace('{number}', '1'),
      }
      if (driver === 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_HYBRID' || driver === 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_ALT' || driver === 'SPP_ENERGYSTORAGE_DRIVER_HUAWEI_EMMA' || driver === 'SPP_ENERGYSTORAGE_DRIVER_FRONIUS_GEN24' ||
        driver === 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_IES' || driver === 'SPP_ENERGYSTORAGE_DRIVER_SOLAX_X1X3_G4' ||
        driver === 'SPP_ENERGYSTORAGE_DRIVER_SIGENERGY') {
        connectionItems = { ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP: this.app.t('Modbus TCP'), ...connectionItems }
      }
      if (driver === 'SPP_ENERGYSTORAGE_DRIVER_SUNGROW_POWERSTACK') {
        // The LC300 controller is reached over Modbus TCP only.
        connectionItems = { ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP: this.app.t('Modbus TCP') }
      }

      return (
        <Box>
          {Measuring}

          <Trans>Connection</Trans>

          <ListSelector
            items={connectionItems}
            icons={[Cable, Cable, Cable]}
            selected={`${params.conn_type || null}`}
            onSelect={(e) => onChangeConnType(e)}
          />

          <Text
            margin="normal"
            label={this.app.t('Slave ID')}
            size="small"
            value={params.slave_id}
            type="number"
            onChange={(e) => onChangeParam('slave_id', parseInt(e.target.value || '0'))}
          />

          {
            (driver === 'SPP_ENERGYSTORAGE_DRIVER_FRONIUS_GEN24' || driver === 'SPP_ENERGYSTORAGE_DRIVER_SIGENERGY') &&
              <Text
              margin="normal"
              label={this.app.t('Energymeter Slave ID')}
              size="small"
              value={params.em_slave_id}
              type="number"
              onChange={(e) => onChangeParam('em_slave_id', parseInt(e.target.value || '0'))}
          />
          }
       </Box>
      )
    }

    return (
      <Box>
        {Measuring}

        <Trans>Connection</Trans>

        <ListSelector
          items={{
            ENERGYSTORAGE_CONN_TYPE_MODBUS_TCP: 'Modbus TCP'
          }}
          icons={[Cable]}
          selected={params.conn_type || null}
          onSelect={(e) => onChangeParam('conn_type', e)}
        />
      </Box>
    )
  }
}
